import { useEffect, useRef, useState } from 'react';
import type { BrowseResult } from '../types';
import { Modal, Stack, Group, TextInput, ActionIcon, Button, UnstyledButton, SimpleGrid, Text, Tooltip } from '@mantine/core';
import ArrowUp from 'lucide-react/dist/esm/icons/arrow-up';
import FolderPlus from 'lucide-react/dist/esm/icons/folder-plus';
import classes from '../editor.module.css';
import { isAudioFile, isVideoFile, isImageFile } from '../utils';
import { useAuth } from '../../../shared/AuthContext';

interface Props {
  open: boolean; browseResult: BrowseResult | null; showDirConfirm: boolean;
  onClose: () => void; onBrowse: (path: string) => void; onSelect: (path: string) => void;
  onCreateFolder?: (parentPath: string, name: string) => void;
  /** When set, images are shown as a grid of previews loaded from this URL instead of a name list. */
  imagePreviewUrl?: (path: string) => string;
}

/** A preview tile; fetched with the user's token, and only once it scrolls into view. */
function ImageTile({ name, url, onClick }: { name: string; url: string; onClick: () => void }) {
  const auth = useAuth();
  const ref = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let objectUrl = '', cancelled = false;
    auth.identity()
      .then(({ token }) => fetch(url, { headers: { authorization: `Bearer ${token}` } }))
      .then(r => r.ok ? r.blob() : Promise.reject(new Error(String(r.status))))
      .then(b => { if (!cancelled) setSrc(objectUrl = URL.createObjectURL(b)); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [visible, url, auth]);

  return (
    <UnstyledButton ref={ref} onClick={onClick} className={classes.thumbTile} title={name}>
      <div className={classes.thumbImage}>
        {src ? <img src={src} alt="" /> : <Text fz="xs" c="dimmed">{failed ? 'Нет превью' : '…'}</Text>}
      </div>
      <Text fz="xs" truncate>{name}</Text>
    </UnstyledButton>
  );
}

export function FilePickerModal({ open, browseResult, showDirConfirm, onClose, onBrowse, onSelect, onCreateFolder, imagePreviewUrl }: Props) {
  const currentPath = browseResult?.path ?? '/';
  const [newFolderName, setNewFolderName] = useState<string | null>(null);

  useEffect(() => { setNewFolderName(null); }, [currentPath]);

  function submitNewFolder() {
    const name = newFolderName?.trim();
    if (name && onCreateFolder) onCreateFolder(currentPath, name);
    setNewFolderName(null);
  }

  const childPath = (name: string) => currentPath === '/' ? `/${name}` : `${currentPath}/${name}`;
  const entries = browseResult?.entries ?? [];
  const tiles = imagePreviewUrl ? entries.filter(e => e.type !== 'dir' && isImageFile(e.name)) : [];
  const rows = imagePreviewUrl ? entries.filter(e => !tiles.includes(e)) : entries;

  function handleEntry(entry: { name: string; type: string }) {
    const newPath = childPath(entry.name);
    if (entry.type === 'dir') onBrowse(newPath);
    else if (!showDirConfirm) onSelect(newPath);
  }

  return (
    <Modal opened={open} onClose={onClose} title="Выбор файла" size="lg" centered>
      <Stack gap="md">
        <Group gap="xs" wrap="nowrap">
          <TextInput readOnly value={currentPath} style={{ flex: 1 }} />
          <Tooltip label="На папку выше" withArrow openDelay={300}><ActionIcon variant="default" size="lg" onClick={() => onBrowse(currentPath.split('/').slice(0, -1).join('/') || '/')} aria-label="Вверх"><ArrowUp size={18} /></ActionIcon></Tooltip>
        </Group>

        <Stack gap={0} style={{ maxHeight: imagePreviewUrl ? 480 : 360, overflowY: 'auto', overflowX: 'auto', border: '1px solid var(--mantine-color-default-border)', borderRadius: 'var(--mantine-radius-sm)' }}>
          {currentPath !== '/' && <UnstyledButton onClick={() => onBrowse(currentPath.split('/').slice(0, -1).join('/') || '/')} className={classes.fileEntry}>.. (Назад)</UnstyledButton>}
          {rows.map(entry => (
            <UnstyledButton key={entry.name} onClick={() => handleEntry(entry)} className={classes.fileEntry}>
              {entry.type === 'dir' ? '📁 ' : (isAudioFile(entry.name) ? '🎵 ' : isVideoFile(entry.name) ? '🎬 ' : '📄 ')}{entry.name}
            </UnstyledButton>
          ))}
          {tiles.length > 0 && imagePreviewUrl && (
            <SimpleGrid cols={4} spacing="xs" p="xs">
              {tiles.map(entry => <ImageTile key={entry.name} name={entry.name} url={imagePreviewUrl(childPath(entry.name))} onClick={() => handleEntry(entry)} />)}
            </SimpleGrid>
          )}
        </Stack>

        {onCreateFolder && showDirConfirm && (
          newFolderName === null ? (
            <Button variant="subtle" leftSection={<FolderPlus size={16} />} onClick={() => setNewFolderName('')} style={{ alignSelf: 'flex-start' }}>Создать папку</Button>
          ) : (
            <Group gap="xs" wrap="nowrap">
              <TextInput placeholder="Имя новой папки" value={newFolderName} onChange={e => setNewFolderName(e.currentTarget.value)} onKeyDown={e => { if (e.key === 'Enter') submitNewFolder(); else if (e.key === 'Escape') setNewFolderName(null); }} style={{ flex: 1 }} autoFocus />
              <Button onClick={submitNewFolder}>Создать</Button>
              <Button variant="default" onClick={() => setNewFolderName(null)}>Отмена</Button>
            </Group>
          )
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>Закрыть</Button>
          {showDirConfirm && <Button onClick={() => onSelect(currentPath)}>Выбрать эту папку</Button>}
        </Group>
      </Stack>
    </Modal>
  );
}
